import Logger from '@eneco/common/log';

/**
 * Get a reference to localStorage if available and functional.
 *
 * On mobile WebView (Android/iOS), after the app has been backgrounded for
 * extended periods, `window.localStorage` can become `null`. Accessing any
 * property on `null` throws a TypeError, bypassing even try-catch wrappers
 * that guard the individual method calls (e.g. `localStorage.setItem`).
 *
 * This helper performs a null-check AND a write-test to ensure the storage
 * is truly usable before returning it.
 */
function getStorage(): Storage | null {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      // Verify storage is actually functional (not just non-null)
      const testKey = '__chatbot_storage_test__';
      window.localStorage.setItem(testKey, '1');
      window.localStorage.removeItem(testKey);
      return window.localStorage;
    }
  } catch {
    // localStorage unavailable: private browsing, WebView restriction,
    // storage quota exceeded, or null after backgrounding
  }
  return null;
}

/**
 * Safe localStorage wrapper that gracefully handles environments where
 * `window.localStorage` is null or non-functional (e.g. mobile WebView
 * after extended backgrounding).
 *
 * All methods silently return safe defaults when storage is unavailable,
 * preventing TypeError crashes while allowing the chatbot to continue
 * operating with in-memory state only.
 */
export const safeStorage = {
  getItem(key: string): string | null {
    try {
      const storage = getStorage();
      if (!storage) {
        return null;
      }
      return storage.getItem(key);
    } catch (error) {
      Logger.error('ss1a2b01', 'safeStorage.getItem failed', { key, error });
      return null;
    }
  },

  setItem(key: string, value: string): boolean {
    try {
      const storage = getStorage();
      if (!storage) {
        Logger.warn('ss1a2b02', 'localStorage unavailable, skipping setItem', { key });
        return false;
      }
      storage.setItem(key, value);
      return true;
    } catch (error) {
      Logger.error('ss1a2b03', 'safeStorage.setItem failed', { key, error });
      return false;
    }
  },

  removeItem(key: string): boolean {
    try {
      const storage = getStorage();
      if (!storage) {
        Logger.warn('ss1a2b04', 'localStorage unavailable, skipping removeItem', { key });
        return false;
      }
      storage.removeItem(key);
      return true;
    } catch (error) {
      Logger.error('ss1a2b05', 'safeStorage.removeItem failed', { key, error });
      return false;
    }
  },
};
